export interface S3ErrorResponse {
  code: string
  message: string
  requestId?: string
  hostId?: string
}

export interface S3ErrorDetails {
  status: number
  code: string
  message: string
  userMessage: string
  requestId?: string
  hostId?: string
}

export const S3_ERROR_CODES = {
  PERMANENT_REDIRECT: 'PermanentRedirect',
  REDIRECT: 'Redirect',
  TEMPORARY_REDIRECT: 'TemporaryRedirect',

  BAD_DIGEST: 'BadDigest',
  CLOUD_TOTAL_ALIVE_SIZE_QUOTA_EXCEED: 'CloudTotalAliveSizeQuotaExceed',
  CREDENTIALS_NOT_SUPPORTED: 'CredentialsNotSupported',
  ENTITY_TOO_SMALL: 'EntityTooSmall',
  ENTITY_TOO_LARGE: 'EntityTooLarge',
  EXPIRED_TOKEN: 'ExpiredToken',
  INCOMPLETE_BODY: 'IncompleteBody',
  INCORRECT_NUMBER_OF_FILES_IN_POST_REQUEST: 'IncorrectNumberOfFilesInPostRequest',
  INLINE_DATA_TOO_LARGE: 'InlineDataTooLarge',
  INVALID_DIGEST: 'InvalidDigest',
  INVALID_ARGUMENT: 'InvalidArgument',
  INVALID_BUCKET_NAME: 'InvalidBucketName',
  INVALID_PART: 'InvalidPart',
  INVALID_PART_ORDER: 'InvalidPartOrder',
  INVALID_REQUEST: 'InvalidRequest',
  BUCKET_MAX_SIZE_EXCEEDED: 'BucketMaxSizeExceeded',
  INVALID_STORAGE_CLASS: 'InvalidStorageClass',
  INVALID_TARGET_BUCKET_FOR_LOGGING: 'InvalidTargetBucketForLogging',
  INVALID_TOKEN: 'InvalidToken',
  INVALID_URI: 'InvalidURI',
  KEY_TOO_LONG_ERROR: 'KeyTooLongError',
  MALFORMED_ACL_ERROR: 'MalformedACLError',
  MALFORMED_POST_REQUEST: 'MalformedPOSTRequest',
  MALFORMED_XML: 'MalformedXML',
  MAX_MESSAGE_LENGTH_EXCEEDED: 'MaxMessageLengthExceeded',
  MAX_POST_PRE_DATA_LENGTH_EXCEEDED_ERROR: 'MaxPostPreDataLengthExceededError',
  METADATA_TOO_LARGE: 'MetadataTooLarge',
  MISSING_REQUEST_BODY_ERROR: 'MissingRequestBodyError',
  MISSING_SECURITY_HEADER: 'MissingSecurityHeader',
  NO_LOGGING_STATUS_FOR_KEY: 'NoLoggingStatusForKey',
  REQUEST_IS_NOT_MULTI_PART_CONTENT: 'RequestIsNotMultiPartContent',
  REQUEST_TIMEOUT: 'RequestTimeout',
  TOKEN_REFRESH_REQUIRED: 'TokenRefreshRequired',
  TOO_MANY_BUCKETS: 'TooManyBuckets',
  UNEXPECTED_CONTENT: 'UnexpectedContent',
  UNRESOLVABLE_GRANT_BY_EMAIL_ADDRESS: 'UnresolvableGrantByEmailAddress',
  USER_KEY_MUST_BE_SPECIFIED: 'UserKeyMustBeSpecified',
  ACCESS_DENIED: 'AccessDenied',
  ACCOUNT_PROBLEM: 'AccountProblem',
  INVALID_ACCESS_KEY_ID: 'InvalidAccessKeyId',
  INVALID_OBJECT_STATE: 'InvalidObjectState',
  INVALID_PAYER: 'InvalidPayer',
  INVALID_SECURITY: 'InvalidSecurity',
  NOT_SIGNED_UP: 'NotSignedUp',
  REQUEST_TIME_TOO_SKEWED: 'RequestTimeTooSkewed',
  SIGNATURE_DOES_NOT_MATCH: 'SignatureDoesNotMatch',
  NO_SUCH_BUCKET: 'NoSuchBucket',
  NO_SUCH_KEY: 'NoSuchKey',
  NO_SUCH_UPLOAD: 'NoSuchUpload',
  NO_SUCH_TAG_SET: 'NoSuchTagSet',
  METHOD_NOT_ALLOWED: 'MethodNotAllowed',
  BUCKET_ALREADY_EXISTS: 'BucketAlreadyExists',
  BUCKET_NOT_EMPTY: 'BucketNotEmpty',
  INVALID_BUCKET_STATE: 'InvalidBucketState',
  OPERATION_ABORTED: 'OperationAborted',
  MISSING_CONTENT_LENGTH: 'MissingContentLength',
  PRECONDITION_FAILED: 'PreconditionFailed',
  INVALID_RANGE: 'InvalidRange',
  TOO_MANY_REQUESTS: 'TooManyRequests',

  INTERNAL_ERROR: 'InternalError',
  NOT_IMPLEMENTED: 'NotImplemented',
  SERVICE_UNAVAILABLE: 'ServiceUnavailable',
  SLOW_DOWN: 'SlowDown'
} as const

export const S3_ERROR_MESSAGES: Record<string, string> = {
  [S3_ERROR_CODES.PERMANENT_REDIRECT]: 'К указанному бакету всегда следует обращаться по указанному в ответе адресу.',
  [S3_ERROR_CODES.REDIRECT]: 'К указанному бакету временно следует обращаться по указанному в ответе адресу.',
  [S3_ERROR_CODES.TEMPORARY_REDIRECT]: 'Редирект на время обновления DNS.',
  [S3_ERROR_CODES.BAD_DIGEST]:
    'Хэш переданных в заголовке Content-MD5 не совпадает с вычисленным на стороне Object Storage.',
  [S3_ERROR_CODES.CLOUD_TOTAL_ALIVE_SIZE_QUOTA_EXCEED]:
    'После загрузки объекта будет превышена соответствующая квота. Запросите повышение квоты или удалите ненужные объекты, а после этого загрузите объект заново.',
  [S3_ERROR_CODES.CREDENTIALS_NOT_SUPPORTED]: 'Учетные данные не поддерживаются.',
  [S3_ERROR_CODES.ENTITY_TOO_SMALL]: 'Загружаемый объект меньше минимально допустимого размера.',
  [S3_ERROR_CODES.ENTITY_TOO_LARGE]: 'Загружаемый объект больше максимально допустимого.',
  [S3_ERROR_CODES.EXPIRED_TOKEN]: 'Срок действия предоставленного токена истек.',
  [S3_ERROR_CODES.INCOMPLETE_BODY]: 'Размер отправленных данных меньше, чем указано в заголовке Content-Length.',
  [S3_ERROR_CODES.INCORRECT_NUMBER_OF_FILES_IN_POST_REQUEST]: 'Метод POST требует передачи строго одного файла.',
  [S3_ERROR_CODES.INLINE_DATA_TOO_LARGE]: 'Данные запроса превышают максимально допустимый размер.',
  [S3_ERROR_CODES.INVALID_DIGEST]: 'Хэш, переданный в заголовке Content-MD5, не верен.',
  [S3_ERROR_CODES.INVALID_ARGUMENT]: 'Недопустимый аргумент.',
  [S3_ERROR_CODES.INVALID_BUCKET_NAME]: 'Недопустимое имя бакета.',
  [S3_ERROR_CODES.INVALID_PART]:
    'Одна и более частей составной загрузки не найдены. Проверьте корректность перечня. Возможно отсутствующие части не были загружены.',
  [S3_ERROR_CODES.INVALID_PART_ORDER]:
    'Перечень частей составной загрузки указан некорректно. Части должны быть отсортированы по возрастанию номера.',
  [S3_ERROR_CODES.INVALID_REQUEST]: 'Используйте AWS4-HMAC-SHA256.',
  [S3_ERROR_CODES.BUCKET_MAX_SIZE_EXCEEDED]: 'Попытка превысить максимальный размер бакета.',
  [S3_ERROR_CODES.INVALID_STORAGE_CLASS]: 'Некорректный класс хранилища.',
  [S3_ERROR_CODES.INVALID_TARGET_BUCKET_FOR_LOGGING]:
    'Бакет не существует или вы не владелец бакета или недостаточно прав у группы доставки логов.',
  [S3_ERROR_CODES.INVALID_TOKEN]: 'Токен неправильно сформирован или некорректен по другой причине.',
  [S3_ERROR_CODES.INVALID_URI]: 'Не получилось разобрать переданный URI.',
  [S3_ERROR_CODES.KEY_TOO_LONG_ERROR]: 'Ключ слишком длинный.',
  [S3_ERROR_CODES.MALFORMED_ACL_ERROR]:
    'Предоставленный XML документ неправильно сформирован или не соответствует схеме.',
  [S3_ERROR_CODES.MALFORMED_POST_REQUEST]: 'Тело запроса не соответствует формату multipart/form-data.',
  [S3_ERROR_CODES.MALFORMED_XML]: 'Предоставленный XML документ неправильно сформирован или не соответствует схеме.',
  [S3_ERROR_CODES.MAX_MESSAGE_LENGTH_EXCEEDED]: 'Превышена допустимая длина запроса.',
  [S3_ERROR_CODES.MAX_POST_PRE_DATA_LENGTH_EXCEEDED_ERROR]: 'Заголовок HTTP-сообщения превысил допустимый размер.',
  [S3_ERROR_CODES.METADATA_TOO_LARGE]: 'Размер заголовков метаданных превысил допустимый размер.',
  [S3_ERROR_CODES.MISSING_REQUEST_BODY_ERROR]: 'Пустое тело запроса.',
  [S3_ERROR_CODES.MISSING_SECURITY_HEADER]: 'Отсутствует необходимый заголовок.',
  [S3_ERROR_CODES.NO_LOGGING_STATUS_FOR_KEY]: 'Отсутствует статус логирования ключа.',
  [S3_ERROR_CODES.REQUEST_IS_NOT_MULTI_PART_CONTENT]: 'Запрос должен содержать данные типа multipart/form-data.',
  [S3_ERROR_CODES.REQUEST_TIMEOUT]: 'Таймаут на чтение/запись.',
  [S3_ERROR_CODES.TOKEN_REFRESH_REQUIRED]: 'Обновите токен.',
  [S3_ERROR_CODES.TOO_MANY_BUCKETS]:
    'После создания бакета будет превышена соответствующая квота. Запросите повышение квоты или удалите ненужные бакеты, а после этого создайте бакет заново.',
  [S3_ERROR_CODES.UNEXPECTED_CONTENT]: 'В запросе не должно быть контента.',
  [S3_ERROR_CODES.UNRESOLVABLE_GRANT_BY_EMAIL_ADDRESS]: 'Не зарегистрированный e-mail.',
  [S3_ERROR_CODES.USER_KEY_MUST_BE_SPECIFIED]: 'Запрос должен содержать указанный в описании ошибки заголовок.',
  [S3_ERROR_CODES.ACCESS_DENIED]: 'Доступ запрещен.',
  [S3_ERROR_CODES.ACCOUNT_PROBLEM]:
    'Проблема с аккаунтом, не позволяющая завершиться операции успешно. Обратитесь в службу поддержки Yandex Cloud.',
  [S3_ERROR_CODES.INVALID_ACCESS_KEY_ID]: 'Неизвестный ключ.',
  [S3_ERROR_CODES.INVALID_OBJECT_STATE]: 'Запрос не может быть выполнен для текущего состояния объекта.',
  [S3_ERROR_CODES.INVALID_PAYER]: 'Доступ к объекту заблокирован.',
  [S3_ERROR_CODES.INVALID_SECURITY]: 'Предоставленные секретные ключи не валидны.',
  [S3_ERROR_CODES.NOT_SIGNED_UP]: 'Для аккаунта не разрешено использование Object Storage.',
  [S3_ERROR_CODES.REQUEST_TIME_TOO_SKEWED]:
    'Слишком большая разница между временем отправки запроса и временем на сервере.',
  [S3_ERROR_CODES.SIGNATURE_DOES_NOT_MATCH]:
    'Предоставленная подпись запроса не соответствует вычисленной Object Storage.',
  [S3_ERROR_CODES.NO_SUCH_BUCKET]: 'Указанного бакета не существует.',
  [S3_ERROR_CODES.NO_SUCH_KEY]: 'Указанный ключ не существует.',
  [S3_ERROR_CODES.NO_SUCH_UPLOAD]: 'Указанной составной загрузки не существует.',
  [S3_ERROR_CODES.NO_SUCH_TAG_SET]: 'Указанному бакету не назначены метки.',
  [S3_ERROR_CODES.METHOD_NOT_ALLOWED]: 'HTTP-метод не применим к заданному ресурсу.',
  [S3_ERROR_CODES.BUCKET_ALREADY_EXISTS]: 'Бакет с таким названием уже существует, выберите другие имя.',
  [S3_ERROR_CODES.BUCKET_NOT_EMPTY]: 'Бакет, который вы удаляете, не пустой.',
  [S3_ERROR_CODES.INVALID_BUCKET_STATE]: 'Запрос не может быть выполнен для текущего состояния бакета.',
  [S3_ERROR_CODES.OPERATION_ABORTED]: 'Конфликтующие условные операции.',
  [S3_ERROR_CODES.MISSING_CONTENT_LENGTH]: 'Добавьте Content-Length в заголовки.',
  [S3_ERROR_CODES.PRECONDITION_FAILED]: 'Не выполнено одно из указанных в запросе условий.',
  [S3_ERROR_CODES.INVALID_RANGE]: 'Неправильный диапазон в заголовке Range.',
  [S3_ERROR_CODES.TOO_MANY_REQUESTS]: 'Слишком много запросов к Object Storage. Снизьте частоту обращений.',
  [S3_ERROR_CODES.INTERNAL_ERROR]: 'Внутренняя ошибка Object Storage. Повторите запрос.',
  [S3_ERROR_CODES.NOT_IMPLEMENTED]: 'Переданный заголовок не обрабатывается Object Storage.',
  [S3_ERROR_CODES.SERVICE_UNAVAILABLE]: 'Object Storage недоступен. Уменьшите частоту ваших запросов.',
  [S3_ERROR_CODES.SLOW_DOWN]: 'Уменьшите частоту ваших запросов.'
} as const
